import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from '../../../shared/IconButton';
import IconLine from '../../../IconLine';
import StateCell from '../../../cells/StateCell';
import { useNavigate } from 'react-router-dom';
import { useFormTemplatesByGroupIdQuery, useLatestFormTemplatesQuery } from '../../../../hooks/queries/useFormTemplatesQuery';
import {
    useCreateFormTemplateMutation,
    useCreateVersionFormTemplateMutation,
    useDeleteFormTemplateMutation,
    useDesactivateFormTemplateMutation,
} from '../../../../hooks/mutations/useFormTemplateMutation';
import { formTemplateStatusEnum, getFormTemplateStatusLabel, permissionLevels } from '../../../../utils/enums';
import { getFormTemplateStatusColor } from '../../../../utils/colors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import GenericAgGridTable from '../../GenericAgGridTable';
import Icon from '../../../shared/Icon';
import { Button } from '@mui/material';
import { FormTemplate } from '../../../../services/apiSLTP/qhse/forms/formTemplates';
import { useAuth } from '../../../../hooks/useAuth';

interface FormHistoryTableProps {
    groupId: string;
}

const FormHistoryTable: React.FC<FormHistoryTableProps> = ({ groupId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const handleOpen = (formId: string) => navigate(`/qhse/forms/${formId}`);

    const { data: forms, isLoading, refetch } = useFormTemplatesByGroupIdQuery({ groupId });
    const { mutate: createNewVersionForm } = useCreateVersionFormTemplateMutation({
        onSuccess: (data) => {
            handleOpen(data.data._id);
            refetch();
        },
    });

    const { mutate: desactivateForm } = useDesactivateFormTemplateMutation({
        onSuccess: () => {
            refetch();
        },
    });

    const { mutate: deleteForm } = useDeleteFormTemplateMutation({
        onSuccess: () => {
            refetch();
        },
    });

    const latestForm = useMemo(() => {
        if (!forms || forms.length === 0) return null;
        return forms.slice().sort((a, b) => (b.version || 0) - (a.version || 0))[0];
    }, [forms]);

    const columnDefs: ColDef<FormTemplate>[] = [
        {
            headerName: capitalizeFirstLetter(t('title')),
            field: 'title',
            initialFlex: 1,
            minWidth: 300,
            colId: 'title',
        },

        {
            headerName: capitalizeFirstLetter(t('actions')),
            colId: 'actions',
            sortable: false,
            filter: false,
            cellRenderer: (params: ICellRendererParams<FormTemplate>) => {
                if (!params.data) return null;
                const form = params.data;
                return (
                    <IconLine>
                        <IconButton iconName="fileOpen" title={t('Ouvrir')} onClick={() => handleOpen(form._id)} />
                        {form.status === formTemplateStatusEnum.PUBLISHED && (
                            <>
                                <IconButton
                                    iconName="cancel"
                                    title={capitalizeFirstLetter(t('desactivate'))}
                                    onClick={() => {
                                        if (window.confirm(t('areYouSureYouWantToDesactivateThisForm'))) {
                                            desactivateForm({ templateId: form._id });
                                        }
                                    }}
                                />
                            </>
                        )}
                        {form.status === formTemplateStatusEnum.DRAFT && (
                            <>
                                <IconButton
                                    iconName="deleteForever"
                                    color="error"
                                    title={capitalizeFirstLetter(t('delete'))}
                                    onClick={() => {
                                        if (window.confirm(t('areYouSureYouWantToDeleteThisForm'))) {
                                            deleteForm({ templateId: form._id });
                                        }
                                    }}
                                />
                            </>
                        )}
                    </IconLine>
                );
            },
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => t(getFormTemplateStatusLabel(params.data?.status)),
            cellRenderer: (params: ICellRendererParams) => (
                <StateCell text={t(getFormTemplateStatusLabel(params.data?.status))} stateColor={getFormTemplateStatusColor(params.data?.status)} />
            ),
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('description')),
            field: 'description',
            colId: 'description',
            initialWidth: 300,
        },
        {
            headerName: capitalizeFirstLetter(t('version')),
            field: 'version',
            colId: 'version',
        },
        {
            headerName: capitalizeFirstLetter(t('answers')),
            //field: 'nbAnswers',
            colId: 'answers',
        },
        {
            headerName: capitalizeFirstLetter(t('createdAt')),
            field: 'createdAt',
            valueGetter: (params) => params.data?.createdAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'createdAt',
        },
        {
            headerName: capitalizeFirstLetter(t('updatedAt')),
            field: 'updatedAt',
            valueGetter: (params) => params.data?.updatedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'updatedAt',
        },
        {
            headerName: capitalizeFirstLetter(t('publishedAt')),
            field: 'publishedAt',
            valueGetter: (params) => params.data?.publishedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'publishedAt',
        },

        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<FormTemplate>[]),
    ];

    return (
        <GenericAgGridTable
            columnDefs={columnDefs}
            rowData={forms?.sort((a, b) => b.version - a.version) || []}
            loading={isLoading}
            exportCsvFileName={`qhse-forms-history-${groupId}`}
            agGridProps={{ getRowId: ({ data }) => data._id }}
            headerRightComponent={
                <>
                    {latestForm?.status === formTemplateStatusEnum.PUBLISHED && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Icon name="postAdd" />}
                            onClick={() => {
                                if (window.confirm(t('areYouSureYouWantToCreateNewVersionForThisForm')))
                                    createNewVersionForm({
                                        templateId: latestForm?._id || '',
                                    });
                            }}
                        >
                            {t('newVersionForm')}
                        </Button>
                    )}
                    {latestForm?.status === formTemplateStatusEnum.DRAFT && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Icon name="edit" />}
                            onClick={() => {
                                handleOpen(latestForm._id);
                            }}
                        >
                            {t('editForm')}
                        </Button>
                    )}
                </>
            }
        />
    );
};

export default FormHistoryTable;
